<template>
  <div :id="id" :class="{'d-flex': horizontal, 'justify-content-between': horizontal}">
    <label :id="idLabel" v-if="inputLabel && !hiddenLabel" :class="customLabelClass">
      {{ inputLabel }}
      <span v-if="isMandatory" class="color-red">* </span>
      <i v-if="tooltipLabel"
        class="fa-duotone fa-circle-info cursor text-info tooltipIcon"
        :title="tooltipLabel">
      </i>
    </label>
    <div v-if="inputType === 'number'" class="inputParentFormWriteGroup" :class="customInputClass">
      <input class="form-control inputAccordionForm" type="number"
        :id="idData"
        :class="errorIndicatorHighlight + ' ' + customHeightClass + ' ' + customInputClass"
        :disabled="disabled"
        :placeholder="placeholderValue" v-model="inputValue"
        :min="minValue" :max="maxValue"
        @blur="verifyIncorrectValue()"
        @input="inputTypingEvent"
        step="any">
        <i v-if="inputIcon" class="fs-17px" :class="inputIcon"></i>
        <i v-if="secondInputIcon" class="fs-17px pl-20px" :class="secondInputIcon"></i>
      </input>
      <slot name="afterInput"></slot>
    </div>
    <div v-else-if="inputType === 'text' || inputType === 'password'" class="inputParentFormWriteGroup" :class="customInputClass">
      <input class="form-control inputAccordionForm" :type="inputType"
        :id="idData"
        :disabled="disabled"
        :class="errorIndicatorHighlight + ' ' + customHeightClass + ' ' + customInputClass"
        :placeholder="placeholderValue" v-model="inputValue"
        :maxlength="inputType === 'text' && maxLength > 0 ? maxLength :''"
        @blur="verifyIncorrectValue()"
        @input="inputTypingEvent">
        <i v-if="inputIcon" class="fs-13px" :class="inputIcon"></i>
        <i v-if="secondInputIcon" class="fs-13px pl-20px" :class="secondInputIcon"></i>
      </input>
      <slot name="afterInput"></slot>
      <slot></slot>
    </div>
    <div v-else-if="inputType === 'textarea'" class="inputParentFormWriteGroup" :class="customInputClass">
      <textarea
        :id="idData"
        :cols="cols"
        :rows="rows"
        :disabled="disabled"
        class="form-control inputAccordionForm"
        :class="errorIndicatorHighlight + ' ' + customHeightClass + customInputClass"
        :placeholder="placeholderValue" v-model="inputValue"
        @input="inputTypingEvent">
      </textarea>
      <slot name="afterInput"></slot>
      <slot></slot>
    </div>
    <div class="formDatePicker" v-else-if="inputType === 'date'" :class="customInputClass">
      <date-picker v-model="inputValue" :format="customFormatter"
        :id="idData"
        :disabled="disabled"
        :placeholder="placeholderValue" :language="languageValue"
        :class="!inputIcon ? errorIndicatorHighlight : errorIndicatorHighlight + ' ' + 'datepicker-icon-input' + ' ' + customHeightClass" input-class="form-control inputAccordionForm"
        :disabledDates="disabledDates">
      </date-picker>
      <i v-if="inputIcon" class="fs-17px pt-6px" :class="inputIcon"></i>
    </div>
    <div class="inputParentFormWriteGroup" v-else-if="inputType === 'datetime'" :class="">
      <input type="datetime-local" v-model="inputValue" format="DD/MM/YYYY H:i:s"
        :id="idData"
        :disabled="disabled"
        :class="errorIndicatorHighlight + ' ' + customHeightClass"
        :placeholder="placeholderValue" :language="languageValue"
        class="form-control inputAccordionForm w-100"
        :min="disabledDateTimes"
        :max="disabledEndDateTime"
        @blur="verifyIncorrectValue()">
      </input>
      <slot name="afterInput"></slot>
    </div>
    <div :id="idData" v-else :class="customInputClass"> <!-- Somes fields like drop-down lists only require a slot -->
      <slot></slot>
    </div>
    <div :id="idError" v-if="regexpErrorIndicator" class="text-danger" :class="customErrorClass">
      {{ $t('errors.is_incorrect', { incorrectValue: inputLabel.toLowerCase() }) }}
    </div>
    <div :id="idError" v-else-if="componentMandatoryErrorIndicator" class="text-danger" Page :class="customErrorClass">
      {{ $t('errors.is_mandatory', { mandatoryValue: inputLabel.toLowerCase() }) }}
    </div>
    <div :id="idError" v-else-if="intervalErrorIndicator" class="text-danger" Page :class="customErrorClass">
      {{ $t('errors.not_in_interval', { outOfRangeValue: inputLabel.toLowerCase() }) }}
    </div>
    <div :id="idError" v-else-if="minErrorIndicator" class="text-danger" Page :class="customErrorClass">
      {{ $t('errors.must_be_superior_to', { fieldName: inputLabel.toLowerCase(), minValue: minValue }) }}
    </div>
    <div :id="idError" v-else-if="maxErrorIndicator" class="text-danger" Page :class="customErrorClass">
      {{ $t('errors.must_be_inferior_than', { fieldName: inputLabel.toLowerCase(), maxValue: maxValue }) }}
    </div>
    <div :id="idError" v-else-if="notZeroErrorIndicator" class="text-danger" Page :class="customErrorClass">
      {{ $t('errors.must_be_superior_to_zero') }}
    </div>
    <div :id="idError" v-else-if="specificErrorIndicator" class="text-danger" :class="customErrorClass" Page>
      {{ specificMessage }}
    </div>
    <div :id="idError" v-else-if="warningIndicator" class="orangeFont" :class="customErrorClass">
      {{ specificMessage }}
    </div>
    <slot name="additionalMessage"></slot>
  </div>
</template>
<script>
/* @author TLV 25/06/2020
* @brief Component utilisé pour les champs à remplir pour envoyer un formulaire
*/

import Datepicker from 'vuejs-datepicker'
import * as lang from 'vuejs-datepicker/src/locale'
import moment from 'moment'

export default {
  name: 'formWriteGroupLabelInput',
  props: {
    id: {
      type: String,
      default: undefined
    },
    colInputSize: {
      type: Number,
      default: 7
    },
    colLabelSize: {
      type: Number,
      default: 5
    },
    hiddenLabel: {
      type: Boolean,
      default: false
    },
    horizontal: {
      type: Boolean,
      default: false
    },
    placeholderValue: {
      type: String
    },
    isMandatory: {
      type: Boolean,
      default: false
    },
    isNotZero: {
      type: Boolean,
      default: false
    },
    inputClass: {
      type: String,
      default: ''
    },
    inputIcon: {
      type: String,
      default: ''
    },
    secondInputIcon: {
      type: String,
      default: ''
    },
    inputLabel: {
      type: String,
      default: ''
    },
    inputType: {
      type: String,
      default: 'text'
    },
    inputValues: {
      type: Array,
      default: null
    },
    value: {
      type: String,
      default: ''
    },
    labelClass: {
      type: String,
      default: ''
    },
    minValue: {
      type: Number,
      default: 0
    },
    maxValue: {
      type: Number,
      default: Number.MAX_VALUE // 1.7976931348623157e+308
    },
    disabled: {
      type: Boolean,
      default: false
    },
    customHeight: {
      type: String,
      default: 'none'
    },
    disablePastDates: {
      type: Boolean,
      default: false
    },
    disablePastDateTimes: {
      type: Boolean,
      default: false
    },
    disableFromDate: {
      type: String,
      default: ''
    },
    disableDateTimesAfter: {
      type: Number,
      default: 0
    },
    mandatoryErrorIndicator: {
      type: Boolean,
      default: false
    },
    regexpErrorIndicator: {
      type: Boolean,
      default: false
    },
    intervalErrorIndicator: {
      type: Boolean,
      default: false
    },
    warningIndicator: {
      type: Boolean,
      default: false
    },
    specificMessage: {
      type: String,
      default: ''
    },
    specificErrorIndicator: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: 0
    },
    tooltipLabel: {
      type: String,
      default: ''
    },
    typingDone: {
      type: Function,
      default: function () {}
    },
    typingInterval: {
      type: Number,
      default: 0
    },
    resize: {
      type: String,
      default: 'none'
    },
    rows: {
      type: String,
      default: '1'
    },
    cols: {
      type: String,
      default: ''
    },
  },
  data () {
    return {
      componentMandatoryErrorIndicator: this.mandatoryErrorIndicator,
      customErrorClass: '',
      customInputClass: '',
      customLabelClass: '',
      displayError: false,
      maxErrorIndicator: false,
      minErrorIndicator: false,
      notZeroErrorIndicator: false,
      oldValue: '',
      today: new Date().toISOString().slice(0, 16)
    }
  },
  components: {
    'date-picker': Datepicker
  },
  computed: {
    customHeightClass () {
      return this.customHeight !== 'none' ? 'h-' + this.customHeight + 'px' : 'h-30px'
    },
    // pseudo v-model avec le component parent
    idLabel () {
      return this.id ? this.id + '-label' : undefined
    },
    idData () {
      return this.id ? this.id + '-data' : Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5) + '-data'
    },
    idError () {
      return this.id ? this.id + '-error' : undefined
    },
    inputValue: {
      get () {
        return (this.inputType === 'number' && this.value === '.') ? 0 : this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    languageValue () {
      return lang[this.$i18n.locale]
    },
    errorIndicatorHighlight () {
      if (this.componentMandatoryErrorIndicator ||
        this.regexpErrorIndicator ||
        this.intervalErrorIndicator ||
        this.maxErrorIndicator ||
        this.minErrorIndicator ||
        this.notZeroErrorIndicator ||
        this.specificErrorIndicator) {
        return 'errorBorder'
      }
      if (this.warningIndicator) {
        return 'warningBorder'
      }
      return ''
    },
    disabledDates () {
      var dates = {}
      if (this.disablePastDates) {
        dates['to'] = new Date(Date.now())
      }
      if (this.disableFromDate) {
        dates['from'] = new Date(this.disableFromDate)
      }
      return dates
    },
    disabledDateTimes () {
      var result  = ''
      if (this.disablePastDateTimes) {
        result = this.today
      }
      return result
    },
    disabledEndDateTime () {
      let result
      if (this.disableDateTimesAfter) {
        let today = new Date()
        let newMonth = today.getMonth() + this.disableDateTimesAfter
        today.setMonth(newMonth)
        result = today.toISOString().slice(0, 16)
      }
      return result
    }
  },
  created () {
    if (this.horizontal) {
      this.customInputClass = 'col-' + this.colInputSize + ' ' + this.inputClass
      this.customLabelClass = 'col-' + this.colLabelSize + ' ' + this.labelClass
      this.customErrorClass = 'offset-' + this.colLabelSize + ' col-' + this.colInputSize + ''
    } else {
      this.customInputClass = this.inputClass
      this.customLabelClass = this.labelClass
    }
    if (this.inputIcon) {
      this.customInputClass += ' d-flex'
    }
  },
  methods: {
    customFormatter (date) {
      return moment(date).format(this.$t('common.datepicker_format_date'))
    },
    inputTypingEvent () {
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(this.typingDone, this.typingInterval)
    },
    verifyIncorrectValue () {
      if (this.Dom7('#' + this.idData).hasClass('display-error')) {
        this.componentMandatoryErrorIndicator = this.isMandatory && (this.inputValue === '' || (this.inputType === 'datetime' && this.inputValue === 'Invalid date'))
        this.minErrorIndicator = this.inputType === 'number' && this.inputValue < this.minValue
        this.maxErrorIndicator = this.inputType === 'number' && this.inputValue > this.maxValue
        this.notZeroErrorIndicator = this.inputType === 'number' && parseFloat(this.inputValue) === 0
        if (this.oldValue !== this.inputValue) {
          this.displayError = false
        } else {
          this.displayError = true
        }
        this.oldValue = this.inputValue
      } else if (this.displayError && this.oldValue === this.inputValue) {
        this.componentMandatoryErrorIndicator = this.isMandatory && (this.oldValue === '' || (this.inputType === 'datetime' && this.oldValue === 'Invalid date'))
        this.minErrorIndicator = this.inputType === 'number' && this.oldValue < this.minValue
        this.maxErrorIndicator = this.inputType === 'number' && this.oldValue > this.maxValue
        this.notZeroErrorIndicator = this.inputType === 'number' && parseFloat(this.oldValue) === 0
      } else {
        this.componentMandatoryErrorIndicator = false
        this.minErrorIndicator = false
        this.maxErrorIndicator = false
        this.notZeroErrorIndicator = false
        this.displayError = false
        this.oldValue = this.inputValue
      }

      this.$emit('blur')
    }
  }
}
</script>
